import React, { useState, useEffect } from 'react';
import api, { UPLOADS_URL } from '../api';

const COMMISSION_COLORS = {
  Open:     { bg: 'rgba(45,106,45,0.3)',  border: '#2d6a2d', text: '#90ee90' },
  Closed:   { bg: 'rgba(80,80,80,0.3)',   border: '#555',    text: '#aaa'    },
  Waitlist: { bg: 'rgba(139,100,0,0.3)',  border: '#8B6400', text: '#f0c060' }
};

function CommissionBadge({ status }) {
  const s = COMMISSION_COLORS[status] || COMMISSION_COLORS.Closed;
  return (
    <span className="commission-badge" style={{ background: s.bg, borderColor: s.border, color: s.text }}>
      Commissions: {status || 'Unknown'}
    </span>
  );
}

function PhotoGallery({ photos, name }) {
  const [current, setCurrent] = useState(0);
  if (!photos || photos.length === 0) return (
    <div className="carousel-empty">🎭</div>
  );
  return (
    <div className="carousel">
      <div className="carousel-img-wrapper">
        <img src={`${UPLOADS_URL}/${photos[current]}`} alt={`${name} photo ${current + 1}`}
          className="carousel-gallery-img" />
        {photos.length > 1 && (
          <>
            <button onClick={() => setCurrent(i => (i - 1 + photos.length) % photos.length)}
              className="carousel-btn carousel-btn-prev">&lsaquo;</button>
            <button onClick={() => setCurrent(i => (i + 1) % photos.length)}
              className="carousel-btn carousel-btn-next">&rsaquo;</button>
            <div className="carousel-dots">
              {photos.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                  className={`carousel-dot${i === current ? ' active' : ' inactive'}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CostumeModal({ maker, onClose }) {
  const [photoIndex, setPhotoIndex] = useState(0);
  const photos = maker.photos || [];

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [onClose]);

  const linkRow = (label, url) => {
    if (!url) return null;
    const href = url.startsWith('http') ? url : 'https://' + url;
    let display = url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    if (/instagram\.com/i.test(url)) display = '@' + url.replace(/^https?:\/\/(www\.)?instagram\.com\/@?/, '').replace(/^@/, '').replace(/\/$/, '');
    else if (/tiktok\.com/i.test(url)) display = '@' + url.replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/, '').replace(/^@/, '').replace(/\/$/, '');
    else if (/facebook\.com/i.test(url)) display = url.replace(/^https?:\/\/(www\.)?facebook\.com\//, '').replace(/\/$/, '');
    else if (/youtube\.com/i.test(url)) display = url.replace(/^https?:\/\/(www\.)?youtube\.com\//, '').replace(/\/$/, '');
    return (
      <div className="link-row">
        <span className="link-label">{label}:</span>
        <a href={href} target="_blank" rel="noopener noreferrer">{display}</a>
      </div>
    );
  };

  return (
    <div className="detail-overlay" onClick={onClose}>
      <div className="detail-modal" onClick={e => e.stopPropagation()}>
        <button className="detail-modal-close" onClick={onClose}>✕</button>

        <div className="detail-modal-header">
          <h2>{maker.display_name}</h2>
          {maker.publish_full_name && maker.full_name && (
            <p className="detail-modal-location">{maker.full_name}</p>
          )}
          <p className="detail-modal-location">
            {maker.city && maker.state ? '📍 ' + maker.city + ', ' + maker.state : maker.country}
          </p>
          <div className="detail-badges">
            <CommissionBadge status={maker.commission_status} />
          </div>
        </div>

        {photos.length > 0 && (
          <div className="detail-photos">
            <img src={`${UPLOADS_URL}/${photos[photoIndex]}`} alt={maker.display_name + ' work'}
              className="detail-main-img" />
            {photos.length > 1 && (
              <div className="detail-thumbs">
                {photos.map((p, i) => (
                  <img key={i} src={`${UPLOADS_URL}/${p}`} alt={'Thumb ' + (i + 1)}
                    onClick={() => setPhotoIndex(i)}
                    className={`detail-thumb${i === photoIndex ? ' active' : ' inactive'}`} />
                ))}
              </div>
            )}
          </div>
        )}

        {maker.bio && (
          <div className="detail-section">
            <h3 className="detail-section-title">About</h3>
            <p>{maker.bio}</p>
          </div>
        )}

        {maker.products_description && (
          <div className="detail-section">
            <h3 className="detail-section-title">Products</h3>
            <p>{maker.products_description}</p>
          </div>
        )}

        {(maker.product_types || maker.price_range || maker.turnaround_time || maker.ships_to) && (
          <div className="detail-section">
            <div className="detail-info-grid">
              {maker.product_types && (
                <div>
                  <div className="info-label">Types</div>
                  <div className="info-value">{maker.product_types}</div>
                </div>
              )}
              {maker.price_range && (
                <div>
                  <div className="info-label">Price Range</div>
                  <div className="info-value">💰 {maker.price_range}</div>
                </div>
              )}
              {maker.turnaround_time && (
                <div>
                  <div className="info-label">Turnaround</div>
                  <div className="info-value">⏱ {maker.turnaround_time}</div>
                </div>
              )}
              {maker.ships_to && (
                <div>
                  <div className="info-label">Ships To</div>
                  <div className="info-value">📦 {maker.ships_to}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {maker.how_to_purchase && (
          <div className="detail-section">
            <h3 className="detail-section-title">How to Order</h3>
            <p>{maker.how_to_purchase}</p>
          </div>
        )}

        <div className="detail-section">
          <h3 className="detail-section-title">Links</h3>
          {linkRow('Website', maker.website)}
          {linkRow('Facebook', maker.facebook)}
          {linkRow('Instagram', maker.instagram)}
          {linkRow('TikTok', maker.tiktok)}
          {linkRow('YouTube', maker.youtube)}
          {linkRow('Other', maker.other_social)}
          {maker.publish_email && maker.email && linkRow('Email', 'mailto:' + maker.email)}
          {maker.publish_phone && maker.phone && (
            <div className="link-row">
              <span className="link-label">Phone:</span>
              <span>{maker.phone}</span>
            </div>
          )}
          {maker.publish_mailing_address && maker.mailing_address && (
            <div className="link-row">
              <span className="link-label">Address:</span>
              <span>{maker.mailing_address}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CostumeCard({ maker, onClick }) {
  const photos = maker.photos || [];
  return (
    <div className="dir-card" onClick={onClick}>
      <PhotoGallery photos={photos} name={maker.display_name} />
      <div className="dir-card-body">
        <h3 className="dir-card-title">{maker.display_name}</h3>
        {maker.city && maker.state && (
          <p className="dir-card-location">📍 {maker.city}, {maker.state}</p>
        )}
        <div className="dir-card-badges">
          <CommissionBadge status={maker.commission_status} />
        </div>
        {maker.product_types && (
          <p className="dir-card-types">
            {maker.product_types.split(',').map(t => t.trim()).filter(Boolean).join(' · ')}
          </p>
        )}
        {maker.price_range && (
          <p className="dir-card-types">💰 {maker.price_range}</p>
        )}
        {maker.bio && (
          <p className="dir-card-bio">{maker.bio}</p>
        )}
      </div>
    </div>
  );
}

function PublicCostumeMakersPage() {
  const [makers, setMakers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCommission, setFilterCommission] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterProduct, setFilterProduct] = useState('');

  useEffect(() => {
    api.get('/api/public/costumes')
      .then(res => { setMakers(res.data); setFiltered(res.data); })
      .catch(() => setError('Failed to load costume makers.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let results = [...makers];
    if (search.trim()) {
      const q = search.toLowerCase();
      results = results.filter(m =>
        (m.display_name || '').toLowerCase().includes(q) ||
        (m.bio || '').toLowerCase().includes(q) ||
        (m.products_description || '').toLowerCase().includes(q) ||
        (m.product_types || '').toLowerCase().includes(q) ||
        (m.city || '').toLowerCase().includes(q) ||
        (m.state || '').toLowerCase().includes(q)
      );
    }
    if (filterCommission) results = results.filter(m => m.commission_status === filterCommission);
    if (filterState) results = results.filter(m => m.state === filterState);
    if (filterProduct) results = results.filter(m => (m.product_types || '').toLowerCase().includes(filterProduct.toLowerCase()));
    setFiltered(results);
  }, [search, filterCommission, filterState, filterProduct, makers]);

  const allStates = [...new Set(makers.map(m => m.state).filter(Boolean))].sort();
  const allProducts = [...new Set(makers.flatMap(m => (m.product_types || '').split(',').map(t => t.trim()).filter(Boolean)))].sort();

  const clearFilters = () => { setSearch(''); setFilterCommission(''); setFilterState(''); setFilterProduct(''); };
  const hasFilters = search || filterCommission || filterState || filterProduct;

  if (loading) return <div className="loading">Loading costume makers...</div>;

  return (
    <div className="directory-page">
      <div className="directory-header">
        <h1>Krampus Costume Makers</h1>
        <p>Find skilled artisans who create custom Krampus costumes, masks, suits, and accessories.</p>
        {error && <p className="error-text">{error}</p>}
      </div>

      <div className="directory-filters">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search makers..." />
        <select value={filterCommission} onChange={e => setFilterCommission(e.target.value)}>
          <option value="">All Commission Status</option>
          <option value="Open">Open</option>
          <option value="Waitlist">Waitlist</option>
          <option value="Closed">Closed</option>
        </select>
        {allProducts.length > 0 && (
          <select value={filterProduct} onChange={e => setFilterProduct(e.target.value)}>
            <option value="">All Product Types</option>
            {allProducts.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        {allStates.length > 0 && (
          <select value={filterState} onChange={e => setFilterState(e.target.value)}>
            <option value="">All States / Provinces</option>
            {allStates.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {hasFilters && (
          <button onClick={clearFilters} className="btn btn-secondary btn-sm">Clear</button>
        )}
      </div>

      <p className="text-muted text-sm">
        {filtered.length} maker{filtered.length !== 1 ? 's' : ''} found
        {makers.length !== filtered.length ? ' (of ' + makers.length + ' total)' : ''}
      </p>

      {filtered.length === 0 ? (
        <div className="dir-empty">
          <p>No costume makers found matching your search.</p>
          <button onClick={clearFilters} className="btn btn-primary mt-2">Clear Filters</button>
        </div>
      ) : (
        <div className="directory-grid">
          {filtered.map(maker => (
            <CostumeCard key={maker.id} maker={maker} onClick={() => setSelected(maker)} />
          ))}
        </div>
      )}

      {selected && <CostumeModal maker={selected} onClose={() => setSelected(null)} />}

      <div className="directory-cta">
        <h3>Are you a costume maker?</h3>
        <p className="text-muted">Get listed in the directory and connect with the Krampus community.</p>
        <a href="/register-costume" className="btn btn-primary">Register as a Costume Maker</a>
      </div>
    </div>
  );
}

export default PublicCostumeMakersPage;
